import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from 'src/store/index';
import { fetchAllQuestions } from 'src/thunks/question';
import _ from 'lodash';

export type State = {
  loading: boolean;
};

export const adapter = createEntityAdapter<Model.Question>({
  selectId: (model) => model.id,
});

const { reducer, actions } = createSlice({
  name: 'questions',
  initialState: adapter.getInitialState<State>({ loading: false }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.Question[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.Question>) {
      adapter.upsertOne(state, action);
    },
    removeEntity(state, action: PayloadAction<string>) {
      adapter.removeOne(state, action.payload);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    retry(state, action: PayloadAction<{ class_topic_content_id: string }>) {
      const moduleQuiz = state.ids.map((id) => state.entities[id]);
      const currentQuizzes = moduleQuiz.filter(
        (q) =>
          q?.class_topic_content_id === action.payload.class_topic_content_id,
      );

      adapter.updateMany(
        state,
        currentQuizzes.map((q) => ({
          id: q?.id!,
          changes: { user_answer: null },
        })),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllQuestions.pending, (state, action) => {
      state.loading = action.meta.arg.shouldLoading !== false;
    });
    builder.addCase(fetchAllQuestions.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchAllQuestions.rejected, (state, action) => {
      state.loading = false;
    });
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.question,
);

export const selectQuestionByClassTopicContentId = createSelector(
  selector.selectAll,
  (state: RootState, class_topic_content_id: string) => {
    return {
      loading: state.model.question.loading,
      class_topic_content_id,
    };
  },
  (questions, { class_topic_content_id, loading }) => {
    const result = questions.filter(
      (q) => q.class_topic_content_id === class_topic_content_id,
    );

    return {
      questions: result,
      loading: loading,
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: (p, n) => _.isEqual(p, n),
    },
  },
);

export { reducer };
export default actions;
